/**
 * Endpoints for the Core::Catalog backend API (admin namespace).
 *
 * Base URL is configured by VITE_YEATI_API_URL which already includes
 * "/api/admin", so paths here are relative to that prefix.
 *
 * Public keys ("products"/"bookings") are kept to minimize churn in the
 * existing UI; internal paths now target /catalog/items, /catalog/bookings...
 */
const CATALOG_BASE = "/catalog";

export const YEATI_LOC_ENDPOINTS = {
  collections: {
    LIST: `${CATALOG_BASE}/collections`,
    SHOW: (id: string) => `${CATALOG_BASE}/collections/${id}`,
    CREATE: `${CATALOG_BASE}/collections`,
    UPDATE: (id: string) => `${CATALOG_BASE}/collections/${id}`,
    DELETE: (id: string) => `${CATALOG_BASE}/collections/${id}`,
    DUPLICATE: (id: string) => `${CATALOG_BASE}/collections/${id}/duplicate`,
  },
  categories: {
    LIST_FOR_COLLECTION: (collectionId: string) =>
      `${CATALOG_BASE}/collections/${collectionId}/categories`,
    SHOW: (id: string) => `${CATALOG_BASE}/categories/${id}`,
    CREATE_FOR_COLLECTION: (collectionId: string) =>
      `${CATALOG_BASE}/collections/${collectionId}/categories`,
    UPDATE: (id: string) => `${CATALOG_BASE}/categories/${id}`,
    DELETE: (id: string) => `${CATALOG_BASE}/categories/${id}`,
  },
  // Public alias kept for compatibility with the existing UI ("products" = items)
  products: {
    LIST: `${CATALOG_BASE}/items`,
    SHOW: (id: string) => `${CATALOG_BASE}/items/${id}`,
    CREATE: `${CATALOG_BASE}/items`,
    UPDATE: (id: string) => `${CATALOG_BASE}/items/${id}`,
    DELETE: (id: string) => `${CATALOG_BASE}/items/${id}`,
    BATCH_DELETE: `${CATALOG_BASE}/items/batch_destroy`,
    // Copie en brouillon de l'article (variantes, tarifs, formulaire…).
    DUPLICATE: (id: string) => `${CATALOG_BASE}/items/${id}/duplicate`,
    // Voyage : remplace toute la grille catégorie × tranche de l'article.
    VOYAGE_GRID: (id: string) => `${CATALOG_BASE}/items/${id}/voyage_grid`,
  },
  bookings: {
    LIST: `${CATALOG_BASE}/bookings`,
    SHOW: (id: string) => `${CATALOG_BASE}/bookings/${id}`,
    CREATE: `${CATALOG_BASE}/bookings`,
    DELETE: (id: string) => `${CATALOG_BASE}/bookings/${id}`,
    // Approbation / refus / annulation en lot depuis la liste (YTI-2023).
    BATCH_TRANSITION: `${CATALOG_BASE}/bookings/batch_transition`,
    // Export Excel des demandes, mêmes filtres `q[...]` que la liste.
    EXPORT_XLSX: `${CATALOG_BASE}/bookings/export_xlsx`,
    // Passage en caisse d'une réservation posée par l'élu : immobilise
    // les dates et pose l'écriture comptable avec la subvention retenue.
    // Pendant console du checkout panier du salarié.
    CHECKOUT: (id: string) => `${CATALOG_BASE}/bookings/${id}/checkout`,
    APPROVE: (id: string) => `${CATALOG_BASE}/bookings/${id}/approve`,
    REJECT: (id: string) => `${CATALOG_BASE}/bookings/${id}/reject`,
    CANCEL: (id: string) => `${CATALOG_BASE}/bookings/${id}/cancel`,
    START: (id: string) => `${CATALOG_BASE}/bookings/${id}/start`,
    REQUEST_RETURN: (id: string) =>
      `${CATALOG_BASE}/bookings/${id}/request_return`,
    EXTEND_LOAN: (id: string) => `${CATALOG_BASE}/bookings/${id}/extend_loan`,
    REFUND: (id: string) => `${CATALOG_BASE}/bookings/${id}/refund`,
    ACCOUNTING_LINES: (id: string) =>
      `${CATALOG_BASE}/bookings/${id}/accounting_lines`,
    COMPENSATE: (id: string) => `${CATALOG_BASE}/bookings/${id}/compensate`,
    SETTLE_WITH_PAYMENT: (id: string) =>
      `${CATALOG_BASE}/bookings/${id}/settle_with_payment`,
    DELIVER: (id: string) => `${CATALOG_BASE}/bookings/${id}/deliver`,
    // Onglet « Opérations » du booking detail - arbre des policies V2
    // avec entrées ledger rattachées. Iso ecom-orders / refunds.
    LEDGER_ENTRIES: (id: string) =>
      `${CATALOG_BASE}/bookings/${id}/ledger_entries`,
    // Admin applique la subvention V2 sur la résa, multi-holder
    // (salarié + ayants droits participants). Accepte soit
    // `subsidy_items: [{holder_type, holder_id, retained_amount}]`
    // (UI saisie), soit `subsidy_choices: {employee, beneficiaries}`
    // (legacy boolean opt-in).
    APPLY_SUBSIDY: (id: string) =>
      `${CATALOG_BASE}/bookings/${id}/apply_subsidy`,
    // Historique des actions admin (drawer timeline). Iso ecom-orders / refunds.
    HISTORY: (id: string) => `${CATALOG_BASE}/bookings/${id}/history`,
    // Voyage : correction de la tranche et des catégories par l'élu,
    // tant que la demande n'est pas validée.
    VOYAGE: (id: string) => `${CATALOG_BASE}/bookings/${id}/voyage`,
  },
  // Mirror EcomOrders : simule l'évaluation V2 pour une résa et chaque
  // holder candidat (salarié + ayants droits) sans rien persister.
  // Le front utilise ça pour afficher les inputs « Montant à retenir »
  // capped au max éligible Pipeline par holder.
  subsidy: { CATALOG_EVALUATIONS: "/subsidy/catalog_evaluations" },
  isbndb: {
    SEARCH: `${CATALOG_BASE}/isbndb/search`,
    IMPORT_COVER: `${CATALOG_BASE}/isbndb/import_cover`,
  },
  slots: {
    LIST_FOR_ITEM: (itemId: string) => `${CATALOG_BASE}/items/${itemId}/slots`,
    CREATE_FOR_ITEM: (itemId: string) => `${CATALOG_BASE}/items/${itemId}/slots`,
    UPDATE: (id: string) => `${CATALOG_BASE}/slots/${id}`,
    DELETE: (id: string) => `${CATALOG_BASE}/slots/${id}`,
  },
  returnRecords: {
    CREATE: `${CATALOG_BASE}/return_records`,
    UPDATE: (id: string) => `${CATALOG_BASE}/return_records/${id}`,
  },
  // Indispos manuelles posées par l'admin (`maintenance` / `blocked` /
  // `other`) sur les items `stock_strategy: calendar`. Distinctes des
  // bookings - un blocage `maintenance` n'a pas de bénéficiaire.
  unavailabilities: {
    LIST_FOR_ITEM: (itemId: string) => `${CATALOG_BASE}/items/${itemId}/unavailabilities`,
    CREATE_FOR_ITEM: (itemId: string) => `${CATALOG_BASE}/items/${itemId}/unavailabilities`,
    UPDATE: (id: string) => `${CATALOG_BASE}/unavailabilities/${id}`,
    DELETE: (id: string) => `${CATALOG_BASE}/unavailabilities/${id}`,
  },
  scoping: {
    SUBVENTION_GROUPES: `${CATALOG_BASE}/scoping/subvention_groupes`,
    LISTE_DIFFUSIONS: `${CATALOG_BASE}/scoping/liste_diffusions`,
  },
  // Modèles de formulaire de réservation, recopiés d'un produit à l'autre.
  formTemplates: {
    LIST: `${CATALOG_BASE}/form_templates`,
    CREATE: `${CATALOG_BASE}/form_templates`,
    UPDATE: (id: string) => `${CATALOG_BASE}/form_templates/${id}`,
    DELETE: (id: string) => `${CATALOG_BASE}/form_templates/${id}`,
  },
  // Settings org-level (liste maître des équipements hébergement,
  // etc.). Édités via la page « Paramètres locations » côté mini.
  settings: {
    SHOW: `${CATALOG_BASE}/settings`,
    UPDATE: `${CATALOG_BASE}/settings`,
  },
  // Import en masse de livres dans une collection `kind: livre`
  // (CSV Comiteo). POST multipart pour créer, GET pour polling.
  bulkImports: {
    LIST: `${CATALOG_BASE}/bulk_imports`,
    CREATE: `${CATALOG_BASE}/bulk_imports`,
    SHOW: (id: string) => `${CATALOG_BASE}/bulk_imports/${id}`,
    RESUME: (id: string) => `${CATALOG_BASE}/bulk_imports/${id}/resume`,
    CANCEL: (id: string) => `${CATALOG_BASE}/bulk_imports/${id}/cancel`,
  },
} as const;
